import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Flag, Check, AlertTriangle, Scale, Beaker, HelpCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Issue categories with icons
const CATEGORIES = [
  { id: 'ingredient_issue', label: 'Ingredient issue', icon: Beaker },
  { id: 'macro_calorie_issue', label: 'Macro / calorie issue', icon: Scale },
  { id: 'portion_amount_issue', label: 'Portion / amount issue', icon: AlertTriangle },
  { id: 'allergen_issue', label: 'Allergen issue', icon: AlertTriangle },
  { id: 'other_issue', label: 'Other issue', icon: HelpCircle },
];

// Sub-issues by category
const SUB_ISSUES = {
  ingredient_issue: [
    'Weird ingredient',
    'Ingredient missing',
    "Ingredient doesn't make sense",
    'Ingredient hard to find',
  ],
  macro_calorie_issue: [
    'Calories seem wrong',
    "Macros don't add up",
    'Protein too low/high',
    'Fat/carbs seem off',
  ],
  portion_amount_issue: [
    'Weird gram amount',
    'Portion too big',
    'Portion too small',
    'Measurement confusing',
  ],
  allergen_issue: [
    'Missing allergen tag',
    'Incorrect allergen tag',
  ],
  other_issue: [
    'Recipe unclear',
    "Doesn't match image",
  ],
};

export default function ReportRecipeButton({ recipeId, recipeName, variant = 'ghost', size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = select category, 2 = select sub-issues
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubIssues, setSelectedSubIssues] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubIssues([]);
    setStep(2);
  };

  const toggleSubIssue = (subIssue) => {
    setSelectedSubIssues(prev => 
      prev.includes(subIssue) 
        ? prev.filter(s => s !== subIssue)
        : [...prev, subIssue]
    );
  };

  const handleSubmit = async () => {
    if (!selectedCategory) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/recipe-reports`, {
        recipe_id: recipeId,
        recipe_name: recipeName,
        category: selectedCategory,
        sub_issues: selectedSubIssues,
      });
      
      toast.success('Thanks! This helps improve the recipe for everyone.');
      handleClose();
    } catch (error) {
      toast.error('Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setStep(1);
    setSelectedCategory(null);
    setSelectedSubIssues([]);
  };

  const getCategoryLabel = (id) => CATEGORIES.find(c => c.id === id)?.label || id;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className="text-gray-500 hover:text-red-500"
      >
        <Flag className="h-4 w-4 mr-1" />
        Report recipe
      </Button>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {step === 1 ? 'What\'s the issue?' : getCategoryLabel(selectedCategory)}
            </DialogTitle>
          </DialogHeader>

          {step === 1 ? (
            <div className="space-y-2 py-4">
              {CATEGORIES.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => handleCategorySelect(id)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                >
                  <Icon className="h-5 w-5 text-gray-500" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-500">Select all that apply (optional):</p>
              <div className="space-y-2">
                {SUB_ISSUES[selectedCategory]?.map((subIssue) => (
                  <button
                    key={subIssue}
                    onClick={() => toggleSubIssue(subIssue)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                      selectedSubIssues.includes(subIssue)
                        ? 'bg-blue-50 border-blue-300'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                      selectedSubIssues.includes(subIssue)
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedSubIssues.includes(subIssue) && (
                        <Check className="h-3 w-3 text-white" />
                      )}
                    </div>
                    <span>{subIssue}</span>
                  </button>
                ))}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1"
                >
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
